// Canonical lists of majors used wherever students self-identify or
// clients filter eligibility.
//
// Two exports:
//
//   MAJORS            — the master list, union of every major across all
//                       partner schools. Use this when no school filter is
//                       applied or as a fallback.
//
//   MAJORS_BY_SCHOOL  — per-school lists keyed by the exact name we store
//                       in allowed_email_domains.university_name. Add a
//                       school here when you have a clean catalog of its
//                       undergraduate programs. Until a school is added,
//                       selecting it falls back to the master MAJORS list
//                       so the picker is never empty.
//
// Keep entries alphabetized. Prefer the most common formal name (e.g.
// "Computer Science", not "CS"). Avoid concentrations and tracks — those
// belong in a free-text "concentration" field, not here.

export const MAJORS: ReadonlyArray<string> = [
  "Accounting",
  "Advertising",
  "Aerospace Engineering",
  "Africana Studies",
  "Agricultural Education and Communication",
  "Agricultural Operations Management",
  "Animal Sciences",
  "Anthropology",
  "Applied Mathematics",
  "Applied Physiology and Kinesiology",
  "Architecture",
  "Art History",
  "Astronomy",
  "Behavioral Sciences",
  "Biochemistry",
  "Biological Engineering",
  "Biology",
  "Biomedical Engineering",
  "Botany",
  "Business Administration",
  "Business Analytics",
  "Chemical Engineering",
  "Chemistry",
  "Civil Engineering",
  "Classical Studies",
  "Coastal and Oceanographic Engineering",
  "Cognitive Science",
  "Communication Sciences and Disorders",
  "Communications",
  "Computer Engineering",
  "Computer Science",
  "Construction Management",
  "Criminology",
  "Cybersecurity",
  "Dance",
  "Data Science",
  "Dietetics",
  "Digital Arts and Sciences",
  "Early Childhood Education",
  "Economics",
  "Educational Studies",
  "Electrical Engineering",
  "Elementary Education",
  "Engineering (General)",
  "English",
  "Entomology and Nematology",
  "Entrepreneurship",
  "Environmental Engineering",
  "Environmental Management",
  "Environmental Science",
  "Family, Youth and Community Sciences",
  "Finance",
  "Food and Resource Economics",
  "Food Science",
  "Foreign Languages and Literatures",
  "Forest Resources and Conservation",
  "Geography",
  "Geological Sciences",
  "Geomatics",
  "Graphic Design",
  "Health Education and Behavior",
  "Health Science",
  "History",
  "Hospitality Management",
  "Human Resources",
  "Industrial and Systems Engineering",
  "Information Systems",
  "Information Technology",
  "Interior Design",
  "International Business",
  "International Studies",
  "Jewish Studies",
  "Journalism",
  "Kinesiology",
  "Landscape Architecture",
  "Linguistics",
  "Management",
  "Management Information Systems",
  "Marine Sciences",
  "Marketing",
  "Materials Science and Engineering",
  "Mathematics",
  "Mechanical Engineering",
  "Media Production Management and Technology",
  "Media Studies",
  "Microbiology and Cell Science",
  "Music",
  "Natural Resource Conservation",
  "Neuroscience",
  "Nuclear Engineering",
  "Nursing",
  "Nutritional Sciences",
  "Operations Research",
  "Pharmaceutical Sciences",
  "Philosophy",
  "Physics",
  "Plant Science",
  "Political Science",
  "Pre-Law",
  "Pre-Med",
  "Psychology",
  "Public Health",
  "Public Policy",
  "Public Relations",
  "Real Estate",
  "Religion",
  "Risk Management and Insurance",
  "Sociology",
  "Software Engineering",
  "Soil, Water, and Ecosystem Sciences",
  "Spanish",
  "Sport Management",
  "Statistics",
  "Strategy",
  "Studio Art",
  "Supply Chain Management",
  "Sustainability and the Built Environment",
  "Sustainability Studies",
  "Systems Engineering",
  "Telecommunication",
  "Theatre",
  "Tourism, Hospitality and Event Management",
  "Urban and Regional Planning",
  "Wildlife Ecology and Conservation",
  "Women’s and Gender Studies",
  "Zoology",
];

/**
 * Per-school undergraduate major catalogs. Add a school here when you
 * have a clean list. Until added, that school falls back to MAJORS.
 * The keys must match `allowed_email_domains.university_name` exactly.
 */
export const MAJORS_BY_SCHOOL: Readonly<Record<string, ReadonlyArray<string>>> = {
  "University of Florida": [
    "Accounting",
    "Advertising",
    "Aerospace Engineering",
    "Agricultural Education and Communication",
    "Agricultural Operations Management",
    "Animal Sciences",
    "Anthropology",
    "Applied Mathematics",
    "Applied Physiology and Kinesiology",
    "Architecture",
    "Art History",
    "Astronomy",
    "Biochemistry",
    "Biological Engineering",
    "Biology",
    "Biomedical Engineering",
    "Botany",
    "Business Administration",
    "Business Analytics",
    "Chemical Engineering",
    "Chemistry",
    "Civil Engineering",
    "Classical Studies",
    "Coastal and Oceanographic Engineering",
    "Cognitive Science",
    "Communication Sciences and Disorders",
    "Communications",
    "Computer Engineering",
    "Computer Science",
    "Construction Management",
    "Criminology",
    "Dance",
    "Data Science",
    "Dietetics",
    "Digital Arts and Sciences",
    "Early Childhood Education",
    "Economics",
    "Educational Studies",
    "Electrical Engineering",
    "Elementary Education",
    "English",
    "Entomology and Nematology",
    "Entrepreneurship",
    "Environmental Engineering",
    "Environmental Management",
    "Environmental Science",
    "Family, Youth and Community Sciences",
    "Finance",
    "Food and Resource Economics",
    "Food Science",
    "Foreign Languages and Literatures",
    "Forest Resources and Conservation",
    "Geography",
    "Geological Sciences",
    "Geomatics",
    "Graphic Design",
    "Health Education and Behavior",
    "Health Science",
    "History",
    "Industrial and Systems Engineering",
    "Information Systems",
    "Interior Design",
    "International Business",
    "International Studies",
    "Jewish Studies",
    "Journalism",
    "Kinesiology",
    "Landscape Architecture",
    "Linguistics",
    "Management",
    "Management Information Systems",
    "Marine Sciences",
    "Marketing",
    "Materials Science and Engineering",
    "Mathematics",
    "Mechanical Engineering",
    "Media Production Management and Technology",
    "Media Studies",
    "Microbiology and Cell Science",
    "Music",
    "Natural Resource Conservation",
    "Neuroscience",
    "Nuclear Engineering",
    "Nursing",
    "Nutritional Sciences",
    "Operations Research",
    "Pharmaceutical Sciences",
    "Philosophy",
    "Physics",
    "Plant Science",
    "Political Science",
    "Pre-Law",
    "Pre-Med",
    "Psychology",
    "Public Health",
    "Public Policy",
    "Public Relations",
    "Real Estate",
    "Religion",
    "Sociology",
    "Soil, Water, and Ecosystem Sciences",
    "Spanish",
    "Sport Management",
    "Statistics",
    "Strategy",
    "Studio Art",
    "Supply Chain Management",
    "Sustainability and the Built Environment",
    "Sustainability Studies",
    "Systems Engineering",
    "Telecommunication",
    "Theatre",
    "Tourism, Hospitality and Event Management",
    "Urban and Regional Planning",
    "Wildlife Ecology and Conservation",
    "Women’s and Gender Studies",
    "Zoology",
  ],
};

/**
 * Resolve which majors should be shown given the selected eligibility
 * schools. Behavior:
 *  - No schools selected → return MAJORS (the union of everything).
 *  - All selected schools have per-school lists → union of those lists.
 *  - Any selected school is missing from MAJORS_BY_SCHOOL → fall back
 *    to MAJORS so the picker is never empty.
 */
export function majorsForSchools(schools: ReadonlyArray<string>): ReadonlyArray<string> {
  if (schools.length === 0) return MAJORS;
  const seen = new Set<string>();
  let missingData = false;
  for (const s of schools) {
    const list = MAJORS_BY_SCHOOL[s];
    if (list) {
      for (const m of list) seen.add(m);
    } else {
      missingData = true;
    }
  }
  if (missingData) return MAJORS;
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** Returns a list of grad-year options sized for the typical undergrad horizon. */
export function gradYearOptions(now: Date = new Date()): number[] {
  const y = now.getFullYear();
  // current year (for fall grads still job hunting) through +5
  const start = y;
  const end = y + 5;
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}
